import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from "chart.js";
import { useElcCapByTech } from "../../hooks/useElcCapByTech";
import { DASHBOARD_COLORS, getEnergyColor } from "../../config/colorScheme";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const LABEL = {
  offshoreWind: "Offshore Wind Farms",
  onshoreWind: "Onshore Wind Farms",
  solar:        "Solar Farms",
//   tidal:        "Tidal Energy Projects",
//   hydro:        "Hydropower Plants",
//   biomass:      "Biomass Power Stations",
//   nuclear:      "Nuclear Micro Plants",
};

const colorFor = (tech, variant) =>
  getEnergyColor(tech, variant) ?? DASHBOARD_COLORS?.neutral?.baseline?.[variant] ?? "#6b7280";

/**
 * Stacked capacity bars by technology (GW) using Chart.js
 */
export default function PowerGenerationLandUsageChart({
  scenario,
  from = 2025,
  to = 2050,
  years = [2025, 2030, 2050],
  gwScale = 1, // set to 1/1000 if your ELC_CAP values are MW
  title = "Electricity Generation System Size Increase",
  className = "bg-white p-6 sm:p-2 md:p-2 rounded-lg shadow-md w-full",
}) {
  const { labels, byTech, techList, loading, error } = useElcCapByTech({
    scenario, from, to, years, gwScale,
  });

  // sort techs by contribution at last label (largest first)
  const sortedTechs = useMemo(() => {
    if (!labels.length) return techList;
    const last = labels[labels.length - 1];
    return [...techList].sort((a, b) => {
      const av = byTech.get(a)?.get(last) ?? 0;
      const bv = byTech.get(b)?.get(last) ?? 0;
      return bv - av;
    });
  }, [techList, labels, byTech]);

  const datasets = useMemo(() => {
    return sortedTechs.map((t) => ({
      label: LABEL[t] || t,
      data: labels.map((y) => byTech.get(t)?.get(y) ?? 0),
      backgroundColor: colorFor(t, "light"),
      borderColor: colorFor(t, "primary"),
      borderWidth: 1,
      stack: "cap",
    }));
  }, [sortedTechs, labels, byTech]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      title: { display: true, text: title },
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, padding: 12 },
        // disable toggling a dataset when clicking legend items
        onClick: () => {},
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed?.y ?? 0;
            return `${ctx.dataset.label}: ${v.toLocaleString(undefined, {
              maximumFractionDigits: 1,
            })} GW`;
          },
        },
      },
    },
    scales: {
      x: { stacked: true, title: { display: true, text: "Year" } },
      y: {
        stacked: true,
        beginAtZero: true,
        title: { display: true, text: "Capacity (GW)" },
        ticks: {
          callback: (v) =>
            Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }),
        },
      },
    },
  }), [title]);

  if (loading) return <div className="p-4 text-sm text-gray-500">Loading capacity by technology…</div>;
  if (error)   return <div className="p-4 text-sm text-red-600">Error: {error.message}</div>;
  if (!labels.length || !datasets.length)
    return <div className="p-4 text-sm text-gray-500">No capacity rows.</div>;

  return (
    <div className={className}>
      <div className="h-96 sm:w-5/5 md:w-4/5 lg:w-4/5 xl:w-3/5 align-center m-auto">
        <Bar data={{ labels: labels.map(String), datasets }} options={options} />
      </div>
    </div>
  );
}

